/**!
 * Created by Leonardo Cheslacki on 17/07/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import {HelpersIcon, HelpersPin} from './../helpers';

import Big from 'big.js';

import config from '../../../../config';

const {default: {domain, routes: {web}, view, text}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class StageView extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        const {frame, pav, current_name} = this.props;

        this.state = {
            data: [] || {},
            frame: frame,
            pav: pav,
        };

        this.$bitmap_view = null;
        this.$thisFrameOld = null;

        this.$helpersPin = HelpersPin(this)({current_name: current_name});

        this.$helpersIcon = HelpersIcon(this)({current_name: current_name})

        _this = this;
    };

    $functions = {
        /**
         *
         * @param event
         * @param number_current
         */
        handlePav: function (event, {number_current}) {
            _this.setState({..._this.state, pav: number_current}, () => {
                let
                    {frame, pav} = _this.state,
                    _this_current = _this.get(),
                    icon_current = `occ_icon_pav${_this.padWithLeadingZeros(number_current, 2)}`,
                    pseudo_current = `occ_pseudo_pav${_this.padWithLeadingZeros(number_current, 2)}`,
                    btn_current = `btn_pav${_this.padWithLeadingZeros(number_current, 2)}`,
                    children = _this_current.children,
                    product = _this.getProduct(frame, pav),
                    image = _this.verifyField(product, 'product.src').value,
                    note = _this.verifyField(product, 'product.note').value;

                if (_this.isArrayIf(children)) {
                    for (let property of children) {
                        if (_this.hasString(property.name, 'occ_icon_pav')) {
                            property.occ_effect.occ_animate.visible = (property.name !== icon_current);
                        }

                        if (_this.hasString(property.name, 'occ_pseudo_pav')) {
                            property.visible = (property.name === pseudo_current);
                        }

                        if (_this.hasString(property.name, 'btn_pav')) {
                            property.visible = (property.name !== btn_current);
                        }
                    }
                }

                _this.setImage(`${_this.$base_uri}/${web.uri_images}/${image}`);
                _this.setTextTypology(note);
            });
        }
    };

    /**
     *
     * @param frame
     * @param pav
     * @returns {*}
     */
    getProduct = (frame, pav) => {
        let
            {data} = this.state,
            array = data.filter((value) => {
                return (
                    (this.verifyField(value, 'product.frame').value.toString() === frame.toString()) &&
                    (this.verifyField(value, 'product.pav').value.toString() === pav.toString())
                );
            });
        return this.firstArray(array);
    };

    /**
     *
     * @param uri
     */
    setImage = (uri) => {
        if (this.getObject(this.$thisFrameOld, 'occ_view')) {
            this.$bitmap_view && this.$thisFrameOld.occ_view.occ_image.removeChild(this.$bitmap_view);
        }

        const {occ_view} = this.getFrame();

        this.$bitmap_view && occ_view.occ_image.removeChild(this.$bitmap_view);
        this.$bitmap_view = (new createjs.Bitmap(uri));

        let
            _scale = 0.7,
            big_button_x = (new Big(this.get().occ_pseudo_pav01.x)),
            big_occ_view_width = (new Big(occ_view.nominalBounds.width)),
            big_occ_view_height = (new Big(occ_view.nominalBounds.height));

        this.$bitmap_view.image.onload = () => {
            let

                scaleX = (this.$bitmap_view.scaleX * this.toFloat(_scale)),
                scaleY = (this.$bitmap_view.scaleY * this.toFloat(_scale)),
                boundsImg = this.$bitmap_view.getBounds(),
                big_width = this.calcSize(1, scaleX, boundsImg.width, true),
                big_height = this.calcSize(1, scaleY, boundsImg.height, true);

            this.$bitmap_view.scaleX = scaleX;
            this.$bitmap_view.scaleY = scaleY;

            occ_view.occ_image.x = this.bigToNumber(big_occ_view_width.minus(big_width.plus(big_occ_view_width.minus(big_button_x))).div(this.$controller.big_2), false, 'up');
            occ_view.occ_image.y = this.bigToNumber(big_occ_view_height.minus(big_height).div(this.$controller.big_2), false, 'up');

            occ_view.occ_image.addChild(this.$bitmap_view);
        }
    };

    setTextTypology = (note) => {
        let occ_title = this.verifyField(this.get(), 'occ_typology.occ_title');

        if (occ_title) {
            occ_title.text = note;
            this.inFunction(occ_title.parent, 'gotoAndPlay') && occ_title.parent.gotoAndPlay('start');
        }
    };

    setTextPav = () => {
        const
            {frame} = this.state,
            {text: {color_text, font, font_size, weight, text_y}} = this.props,
            {children} = this.get();

        if (this.isArrayIf(children)) {
            for (let property of children) {
                if (this.hasString(property.name, 'occ_text_pav')) {
                    let product = this.getProduct(frame, this.toNumber(property.name));

                    !this.verifyField(property, 'state.original') && this.setStateOccurrence(property, {original: {y: property.y}});

                    let y = property.state.original.y + text_y;

                    this.$helpersPin.setText(property, this.verifyField(product, 'product.text').value, color_text, font, font_size, weight, null, y, false);
                }
            }
        }
    };

    startView = () => {
        const
            _this_current = this.get(),
            children = _this_current.children,
            {current_name} = this.props;

        if (this.isArrayIf(children)) {
            for (let property of children) {
                let name = property.name;

                if (this.hasString(name, 'btn_pav')) {
                    let
                        number_current = this.toNumber(name),
                        occ = this.padWithLeadingZeros(number_current, 2),
                        handle_name = this.toHandleName(occ, 'paginate');

                    this[handle_name] && property.off('mousedown', this[handle_name]);
                    this[handle_name] = property.on('mousedown', this.$functions.handlePav, false, false, {
                        number_current: number_current
                    });
                }
            }
        }

        this.main.on('dynamic_paginate', ({number_current, _this_frame_old}) => {
            this.$thisFrameOld = _this_frame_old;
            this.setState({...this.state, frame: number_current}, () => {
                let
                    {frame, pav} = this.state,
                    product = this.getProduct(frame, pav),
                    image = this.verifyField(product, 'product.src').value,
                    note = this.verifyField(product, 'product.note').value;

                this.setImage(`${this.$base_uri}/${web.uri_images}/${image}`);

                this.setTextPav();

                this.setTextTypology(note);
            });
        }, current_name);
    };

    initView = () => {
        const
            _this_current = this.get(),
            {xml} = this.props,
            pad_number_frame = this.padWithLeadingZeros(view.frame, 2),
            pad_number_pav = this.padWithLeadingZeros(view.pav, 2),
            occ_pseudo_frame = this.verifyField(_this_current, `occ_pseudo_frame${pad_number_frame}`),
            occ_pseudo_pav = this.verifyField(_this_current, `occ_pseudo_pav${pad_number_pav}`),
            btn_frame = this.verifyField(_this_current, `btn_frame${pad_number_frame}`),
            btn_pav = this.verifyField(_this_current, `btn_pav${pad_number_pav}`);

        this.$helpersIcon.disabledAnimate(_this_current)(view.frame, 'frame');

        this.$helpersIcon.disabledAnimate(_this_current)(view.pav, 'pav');

        occ_pseudo_frame && (occ_pseudo_frame.visible = true);

        occ_pseudo_pav && (occ_pseudo_pav.visible = true);

        btn_frame && (btn_frame.visible = false);

        btn_pav && (btn_pav.visible = false);

        this.getForm(`${this.$base_uri || domain}/${web.uri_xml}/${xml}`, {}, {
            done: (response) => {
                let
                    object = this.firstArray(this.xmlToJson(response)),
                    products = this.getObject(object, 'products');

                this.setState({...this.state, data: products}, () => {
                    this.startView();
                    this.setTextPav();
                });
            },
            fail: (response) => {
                console.log(response);
            },
            always: () => {
                let
                    {frame, pav} = this.state,
                    product = this.getProduct(frame, pav),
                    image = this.verifyField(product, 'product.src').value,
                    note = this.verifyField(product, 'product.note').value;

                this.setImage(`${this.$base_uri}/${web.uri_images}/${image}`);

                this.setTextTypology(note);

                let occ_title = this.verifyField(_this_current, 'occ_typology.occ_title');

                if (occ_title) {
                    occ_title.y = occ_title.y + 8;
                }
            }
        });
    };
}

/**
 *
 * @type {{}}
 */
StageView.propTypes = {
    current_name: PropTypes.string,
    xml: PropTypes.string,
    frame: PropTypes.number,
    pav: PropTypes.number,
    text: PropTypes.shape({
        color_text: PropTypes.string,
        font: PropTypes.string,
        font_size: PropTypes.number,
        weight: PropTypes.oneOf(['normal', 'bold'])
    }),
};

/**
 *
 * @type {{}}
 */
StageView.defaultProps = {
    current_name: null,
    ...view,
    text: {...view.text, ...text}
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageView, {className: 'StageView'});
